// Reusable label + control + error stack used by every step of the
// property wizard.
//
// The wizard fields are tightly bounded by the visual spec — short
// uppercase labels, generous spacing, inline error in destructive
// color. Co-located here so each step doesn't reinvent the same row.

import { useId, type ReactNode } from "react";

import { cn } from "@/lib/utils";

interface WizardFieldProps {
  label: string;
  /** When supplied, the field is rendered with a required-aria marker. */
  required?: boolean | undefined;
  /** Pass an error string to surface it under the control in destructive color. */
  error?: string | undefined;
  /** Optional hint shown under the control when there is no error. */
  hint?: string | undefined;
  className?: string | undefined;
  /**
   * Receives the generated id so the control can wire `id` + `aria-describedby`.
   * Use the `<WizardField>` render-prop style only when the underlying control
   * is a native input that needs the matching id.
   */
  children: ReactNode | ((id: string) => ReactNode);
}

export function WizardField({
  label,
  required,
  error,
  hint,
  className,
  children,
}: WizardFieldProps) {
  const id = useId();
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label
        htmlFor={id}
        className="text-[12px] font-semibold text-ink-soft"
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-1 text-destructive">
            *
          </span>
        ) : null}
      </label>
      {typeof children === "function" ? children(id) : children}
      {error ? (
        <p id={errorId} role="alert" className="text-[12px] text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chip pickers
// ---------------------------------------------------------------------------

interface ChipOption<T> {
  value: T;
  label: string;
}

interface WizardChipPickerProps<T extends string | number> {
  options: readonly ChipOption<T>[];
  value?: T | undefined;
  onChange: (next: T) => void;
  /** Variant of chip styling — "accent" (primary tint), "ink" (foreground fill). */
  tone?: "accent" | "ink" | undefined;
  /** Accessibility label for the group. */
  ariaLabel?: string | undefined;
}

/**
 * Multi-option chip-group selector. Single selection only. The "accent"
 * tone is used for chips where the active state can blend with the page
 * (property type, furniture); the "ink" tone is used where the selection
 * should pop (bedroom counter).
 */
export function WizardChipPicker<T extends string | number>({
  options,
  value,
  onChange,
  tone = "accent",
  ariaLabel,
}: WizardChipPickerProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="flex flex-wrap gap-1.5"
    >
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => onChange(opt.value)}
            className={cn(
              "rounded-lg border px-3.5 py-1.5 text-[12px] transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isActive && tone === "accent" &&
                "border-primary bg-primary/10 font-semibold text-primary",
              isActive && tone === "ink" &&
                "border-primary bg-primary font-semibold text-primary-foreground",
              !isActive &&
                "border-border bg-card text-ink-soft hover:bg-muted",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
